'use client';

import styles from './handbook.module.css';
import Navbar from '@components/Navbar';
import Footer from '@components/footer/Footer';

export default function EducatorHandbookPage() {
  return (
    <>
      <Navbar />

      <div className={styles.mainContainer}>
        {/* Hero */}
        <header className={styles.siteHeader}>
          <div className={`${styles.container} ${styles.headerInner}`}>
            <div>
              <h1 className={styles.heroTitle}>Educator Handbook</h1>
              <p className={styles.heroText}>
                Clear guidelines to help you create safe, engaging, and high-quality classes on PenguinLearn.
              </p>
            </div>
          </div>
        </header>

        {/* Contents / TOC */}
        <aside
          aria-labelledby="toc-heading"
          className={`${styles.container} ${styles.esaContent}`}
        >
          <h2 id="toc-heading" className={styles.sectionHeading}>Contents</h2>
          <ol className={styles.tocList}>
            <li><a className={styles.tocLink} href="#welcome">Welcome &amp; Mission</a></li>
            <li><a className={styles.tocLink} href="#conduct">Code of Conduct</a></li>
            <li><a className={styles.tocLink} href="#communication">Communication Standards</a></li>
            <li><a className={styles.tocLink} href="#listings">How to List Your Class</a></li>
            <li><a className={styles.tocLink} href="#quality">Class Quality</a></li>
            <li><a className={styles.tocLink} href="#safety">Safety &amp; Background Checks</a></li>
            <li><a className={styles.tocLink} href="#payouts">Payouts &amp; Fees</a></li>
            <li><a className={styles.tocLink} href="#scheduling">Scheduling, Cancellations &amp; Make-ups</a></li>
            <li><a className={styles.tocLink} href="#privacy">Student Privacy</a></li>
            <li><a className={styles.tocLink} href="#support">Support &amp; Contact</a></li>
          </ol>
        </aside>

        {/* Sections */}
        <section id="welcome" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="welcome-heading">
          <h2 id="welcome-heading" className={styles.sectionHeading}>Welcome &amp; Mission</h2>
          <p className={styles.contentText}>
            Welcome to PenguinLearn! Our mission is to make live, high-quality learning accessible and affordable
            for families while empowering educators to earn fairly and teach what they love.
          </p>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Teach subjects you’re passionate about.</li>
            <li className={styles.listItem}>Set your own schedule and pricing.</li>
            <li className={styles.listItem}>
              Follow our{' '}
              <a
                className={styles.linkLearnMore}
                href="/footer/safety"
              >
                safety
              </a>
              ,{' '}
              <a
                className={styles.linkLearnMore}
                href="/footer/privacy-policy"
              >
                privacy
              </a>
              , and quality guidelines.
            </li>
          </ul>
        </section>

        <section id="conduct" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="conduct-heading">
          <h2 id="conduct-heading" className={styles.sectionHeading}>Code of Conduct</h2>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Maintain a respectful, inclusive learning environment.</li>
            <li className={styles.listItem}>Use age-appropriate language and materials.</li>
            <li className={styles.listItem}>Communicate professionally with families and students.</li>
          </ul>
        </section>

        <section id="communication" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="communication-heading">
          <h2 id="communication-heading" className={styles.sectionHeading}>Communication Standards</h2>
          <p className={styles.contentText}>
            Respond to parent inquiries within <strong>24 hours</strong> when possible. Keep communication
            on the PenguinLearn platform for everyone’s safety and privacy.
          </p>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Post clear class descriptions with objectives and prerequisites.</li>
            <li className={styles.listItem}>Send reminders or updates before each session if needed.</li>
            <li className={styles.listItem}>Use our messaging tools to share materials and recap notes.</li>
          </ul>
        </section>

        <section id="listings" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="listings-heading">
          <h2 id="listings-heading" className={styles.sectionHeading}>How to List Your Class</h2>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>
              From the Navbar, click <strong>For Tutors</strong>. Then click the <strong>Create a Course</strong>
            </li>
            <li className={styles.listItem}>
              Complete all fields to the best of your ability. We’ll review your submission and may suggest edits before it goes live.
              After publication, you can update any part of your listing at any time.
            </li>
            <li className={styles.listItem}>
              Questions? Email{' '}
              <a className={styles.linkLearnMore} href="mailto:support@penguinlearn.com">
                support@penguinlearn.com
              </a>.
            </li>
          </ul>
        </section>

        <section id="quality" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="quality-heading">
          <h2 id="quality-heading" className={styles.sectionHeading}>Class Quality</h2>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Title and description must match what you will teach.</li>
            <li className={styles.listItem}>Include supply lists, software needs, and any parental guidance.</li>
            <li className={styles.listItem}>Use engaging slides, demos, or short videos where helpful.</li>
            <li className={styles.listItem}>Start on time do a 5 minute tech check before class.</li>
          </ul>
        </section>

        <section id="safety" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="safety-heading">
          <h2 id="safety-heading" className={styles.sectionHeading}>Safety &amp; Background Checks</h2>
          <p className={styles.contentText}>
            To protect learners, educators are required to pass a background check.
          </p>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Use only approved video tools and meeting links.</li>
            <li className={styles.listItem}>Report any safety concerns to support immediately.</li>
            <li className={styles.listItem}>Follow classroom recording and privacy rules.</li>
            <li className={styles.listItem}>Do not share personal contact info with students or parents.</li>
          </ul>
        </section>

        <section id="payouts" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="payouts-heading">
          <h2 id="payouts-heading" className={styles.sectionHeading}>Payouts &amp; Fees</h2>
          <p className={styles.contentText}>
            PenguinLearn processes payouts on a regular schedule (Sunday or Monday) to your connected account. You set your price; our
            <strong> 20% platform fee</strong> is applied.
          </p>
          <p className={styles.contentText}>
            Example: if you charge $50 for a class, you keep $40. The 20% fee helps cover platform development and maintenance,
            payment processing, customer support, and marketing to attract students.
          </p>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Keep your payout details up to date.</li>
            <li className={styles.listItem}>Refunds and cancellations may affect the final payout for a class.</li>
          </ul>
        </section>

        <section id="scheduling" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="scheduling-heading">
          <h2 id="scheduling-heading" className={styles.sectionHeading}>Scheduling, Cancellations &amp; Make-ups</h2>
          <ul className={styles.bulletedList}>
            <li className={styles.listItem}>Publish available times clearly in your listing.</li>
            <li className={styles.listItem}>If you must cancel, notify families promptly with options.</li>
            <li className={styles.listItem}>Offer reasonable make-ups when appropriate.</li>
          </ul>
        </section>

        <section id="privacy" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="privacy-heading">
          <h2 id="privacy-heading" className={styles.sectionHeading}>Student Privacy</h2>
          <p className={styles.contentText}>
            Respect learner privacy. Share class materials only within the class. Do not collect unnecessary personal data.
          </p>
        </section>

        <section id="support" className={`${styles.container} ${styles.esaContent}`} aria-labelledby="support-heading">
          <h2 id="support-heading" className={styles.sectionHeading}>Support &amp; Contact</h2>
          <p className={styles.contentText}>
            Need help? Email{' '}
            <a className={styles.linkLearnMore} href="mailto:support@penguinlearn.com">
              support@penguinlearn.com
            </a>. For urgent issues, include your class title, time, and a brief description.
          </p>
        </section>

        <Footer />
      </div>
    </>
  );
}